import translate from '@translate';
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import api from '@api';
import moment from 'moment';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Avatar, { AvatarProps } from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Select from 'src/components/Select';
import Loading from 'src/components/Loading/intex';
import { OptionType } from 'src/components/Select';
import { colors, sizes } from 'src/themes/default';
import {
    IntegrationMappingHistoryBlock,
    IntegrationMappingHistoryBlockKind,
    IntegrationMappingHistoryData,
    IntegrationMappingHistorySection,
    IntegrationMappingHistoryUpdate
} from 'src/types/integrationMappingHistory';
import {
    formatHistoryCellValue,
    IntegrationMappingHistoryFormatContext
} from './integrationMappingHistoryCellFormat';

export interface IntegrationMappingHistoryPropsType {
    providerId: number;
    reloadKey: number;
    /** Kódy z API přemapovat na stejné popisky jako ve formuláři (Select). */
    formatContext?: IntegrationMappingHistoryFormatContext;
}

const SECTION_FILTER_ALL = 'all';

const sectionFilterSource: { value: string; labelKey: string }[] = [
    { value: SECTION_FILTER_ALL, labelKey: 'integrationDetail.historySectionAll' },
    { value: 'statuses', labelKey: 'integrationDetail.statuses' },
    { value: 'shippingMethods', labelKey: 'integrationDetail.shippingMethods' },
    { value: 'paymentMethods', labelKey: 'integrationDetail.paymentMethods' },
    { value: 'others', labelKey: 'integrationDetail.others' }
];

const StyledAvatar = styled(Avatar)<AvatarProps>(({ theme }) => ({
    color: colors.primary_O,
    backgroundColor: colors.primary_W,
    border: '1px solid',
    borderColor: colors.primary_O,
    width: theme.spacing(4),
    height: theme.spacing(4),
    flexShrink: 0
}));

const HistoryHeader = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '8px'
});

const BlockHeader = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    padding: '4px 0',

    '& .MuiTypography-subtitle2': {
        fontWeight: sizes.bold_W
    }
});

type HistoryGroupBlock = {
    block: IntegrationMappingHistoryBlock;
    section: IntegrationMappingHistorySection | null;
    blockId: string;
};

type HistoryGroup = {
    created_at?: string;
    user?: IntegrationMappingHistoryUpdate['user'];
    blocks: HistoryGroupBlock[];
};

const userLabel = (user: IntegrationMappingHistoryUpdate['user']): string => {
    if (!user) {
        return '';
    }
    if (typeof user === 'string') {
        return user;
    }
    return `${user.NAME} ${user.SURNAME}`.trim();
};

const blockKindRank = (kind: IntegrationMappingHistoryBlockKind): number => {
    if (kind === 'new' || kind === 'set') {
        return 0;
    }
    if (kind === 'edited') {
        return 1;
    }
    return 2;
};

const orderBlocks = (blocks: IntegrationMappingHistoryBlock[]): IntegrationMappingHistoryBlock[] =>
    [...blocks].sort((a, b) => blockKindRank(a.kind) - blockKindRank(b.kind));

const buildBlockId = (
    createdAt: string | undefined,
    section: IntegrationMappingHistorySection | null,
    block: IntegrationMappingHistoryBlock,
    index: number
): string =>
    `${createdAt ?? 'unknown'}-${section ?? 'none'}-${block.messageKey}-${block.kind}-${index}-${block.rows
        .map((row) => row.uid ?? row.eshopUid ?? '')
        .join(',')}`;

const groupUpdates = (updates: IntegrationMappingHistoryUpdate[]): HistoryGroup[] => {
    const groups: HistoryGroup[] = [];

    [...updates]
        .reverse()
        .forEach((item, updateIndex) => {
            const orderedBlocks = orderBlocks(item.data?.blocks ?? []);
            const section = item.data?.section ?? null;
            const groupBlocks: HistoryGroupBlock[] = orderedBlocks.map((block, blockIndex) => ({
                block,
                section,
                blockId: buildBlockId(item.created_at, section, block, updateIndex * 100 + blockIndex)
            }));

            if (groupBlocks.length === 0) {
                return;
            }

            const lastGroup = groups[groups.length - 1];
            const sameMoment =
                lastGroup &&
                lastGroup.created_at === item.created_at &&
                userLabel(lastGroup.user) === userLabel(item.user);

            if (sameMoment) {
                lastGroup.blocks.push(...groupBlocks);
                return;
            }

            groups.push({
                created_at: item.created_at,
                user: item.user,
                blocks: groupBlocks
            });
        });

    return groups;
};

const getBlockIcon = (kind: IntegrationMappingHistoryBlockKind): ReactNode => {
    if (kind === 'new' || kind === 'set') {
        return <AddIcon fontSize="small" />;
    }
    if (kind === 'edited') {
        return <EditIcon fontSize="small" />;
    }
    return <DeleteIcon fontSize="small" />;
};

const IntegrationMappingHistory: FC<IntegrationMappingHistoryPropsType> = (props) => {
    const [data, setData] = useState<IntegrationMappingHistoryData | null>(null);
    const [loading, setLoading] = useState(false);
    const [sectionFilter, setSectionFilter] = useState<string>(SECTION_FILTER_ALL);
    const [expandedBlocks, setExpandedBlocks] = useState<Record<string, boolean>>({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const query =
                sectionFilter !== SECTION_FILTER_ALL ? `?section=${encodeURIComponent(sectionFilter)}` : '';
            const res = await api('get', `api/secure/providers/${props.providerId}/integration-mapping/history${query}`);
            const body = res.data as { data?: IntegrationMappingHistoryData; success?: boolean };
            setData(body.data ?? { updates: [] });
        } catch (e) {
            console.error(e);
            setData({ updates: [] });
        } finally {
            setLoading(false);
        }
    }, [props.providerId, sectionFilter]);

    useEffect(() => {
        void load();
    }, [load, props.reloadKey]);

    const historyGroups = useMemo(() => groupUpdates(data?.updates ?? []), [data?.updates]);

    const toggleBlock = (blockId: string) => {
        setExpandedBlocks((prev) => ({
            ...prev,
            [blockId]: !prev[blockId]
        }));
    };

    const renderBlockTable = (
        block: IntegrationMappingHistoryBlock,
        section: IntegrationMappingHistorySection | null
    ) => {
        const ctx = props.formatContext;
        const formatCell = (col: string, cell: string): string => {
            if (!ctx || !section) {
                return cell;
            }
            return formatHistoryCellValue(section, block, col, cell, ctx);
        };

        return (
            <Table size="small" component={Paper} variant="outlined" sx={{ mt: 1, mb: 2 }}>
                <TableHead>
                    <TableRow>
                        {block.columns.map((col) => (
                            <TableCell key={col}>{translate(`integrationMappingHistory.columns.${col}`)}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {block.rows.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                            {block.columns.map((col) => (
                                <TableCell key={col}>{formatCell(col, row[col] ?? '')}</TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    };

    const renderGroup = (group: HistoryGroup, groupIndex: number) => {
        const date = group.created_at ? moment(group.created_at) : null;

        return (
            <Paper key={groupIndex} sx={{ p: 2, mb: 2 }}>
                <HistoryHeader>
                    <Typography variant="caption" color="text.secondary">
                        {date ? date.format('LLL') : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {userLabel(group.user)}
                    </Typography>
                </HistoryHeader>
                {group.blocks.map(({ block, section, blockId }) => (
                    <Box key={blockId} sx={{ mb: 1 }}>
                        <BlockHeader
                            role="button"
                            tabIndex={0}
                            onClick={() => toggleBlock(blockId)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    toggleBlock(blockId);
                                }
                            }}
                        >
                            <StyledAvatar>{getBlockIcon(block.kind)}</StyledAvatar>
                            <Typography variant="subtitle2">{translate(block.messageKey)}</Typography>
                        </BlockHeader>
                        <Collapse in={!!expandedBlocks[blockId]}>
                            {renderBlockTable(block, section)}
                        </Collapse>
                    </Box>
                ))}
            </Paper>
        );
    };

    const selectOptions: OptionType[] = sectionFilterSource.map((option) => ({
        value: option.value,
        label: translate(option.labelKey)
    }));

    return (
        <Stack spacing={2}>
            <Box sx={{ width: { xs: '100%', sm: '25%' }, maxWidth: { sm: '25%' } }}>
                <Select
                    name="integrationMappingHistorySection"
                    label={translate('integrationDetail.historySectionFilter')}
                    options={selectOptions}
                    value={sectionFilter}
                    onChange={(value, _error) => {
                        setSectionFilter(String(value ?? SECTION_FILTER_ALL));
                    }}
                    fullWidth
                />
            </Box>
            {loading && <Loading />}
            {!loading && historyGroups.length === 0 && (
                <Typography variant="body2">{translate('integrationDetail.historyEmpty')}</Typography>
            )}
            {!loading && historyGroups.length > 0 ? historyGroups.map(renderGroup) : null}
        </Stack>
    );
};

export default IntegrationMappingHistory;
